import { TUNABLES } from "../config";
import type { TelemetryEvent } from "../telemetry/bus";
import { count, isRecord, jsonRecord, listOf, MAX_ID_CHARS, oneOf, text } from "./protocol";

/**
 * The playtest's events (B3.9, ADR-0016, the plan's analytics tree): which of the game's telemetry
 * events leave the page, in what envelope, and how the server reads a batch of them. Shared by the
 * page (`src/telemetry/beacon.ts`) and the Worker (`server/cloudflare/eventsEndpoint.ts`), so both
 * read one list.
 *
 * Nothing personal travels: no name (the visitor's name stays on the page), no address, no device.
 * A batch carries the page's identity (`src/front/identity.ts`: a random guest ID of the tab or of
 * the browser, or since B3.10.1 a signed-in account's keyed ID), a random ID of the page load, the build's
 * commit, and per event its type, the milliseconds since the page loaded, the duel's code and the
 * event's own numbers and words. Whether anything is sent at all is `eventsMode`: off, our own
 * test runs only, or on, which is the owner's word after the privacy note (N31, D-022).
 */
export const RECORDED_TYPES = [
  "page_landed",
  "lobby_joined",
  "lobby_left",
  "match_started",
  "first_command",
  "run_end",
  "match_quit",
  "socket_dropped",
  "match_rejoined",
  "match_lost",
  "match_desync",
  "seat_chosen",
  "error_uncaught",
  "feedback_sent",
  "bug_reported",
  "signin_shown",
  "signin_done",
  "page_left",
] as const satisfies readonly TelemetryEvent["type"][];

export type RecordedType = (typeof RECORDED_TYPES)[number];

/**
 * B3.10: the events that carry what a player wrote, in their `text` field, up to
 * `maxFeedbackChars`; every other word an event carries is cut to `maxTextChars`.
 */
const WRITTEN: ReadonlySet<string> = new Set(["feedback_sent", "bug_reported"]);

function textLimit(type: string, key: string): number {
  return WRITTEN.has(type) && key === "text"
    ? TUNABLES.events.maxFeedbackChars
    : TUNABLES.events.maxTextChars;
}

/** An event's own fields, as numbers, words and yes-or-no. */
export type EventData = Readonly<Record<string, number | string | boolean>>;

export interface RecordedEvent {
  readonly type: RecordedType;
  /** Milliseconds since the page loaded. */
  readonly at: number;
  /** The duel's code or the run's seed text, when the event belongs to one. */
  readonly game?: string;
  readonly data: EventData;
}

export interface EventBatch {
  /** The page's identity (`src/front/identity.ts`): a guest's random ID, or an account's keyed ID. */
  readonly guest: string;
  /** A random ID of this page load. */
  readonly session: string;
  /** The build's commit. */
  readonly build: string;
  readonly events: readonly RecordedEvent[];
}

/**
 * A switch of what the playtest page collects: off, nothing; self, only an address that asks for
 * it (`?<key>=on`, our test runs); on, every page. Events since B3.9; the feedback and the kept
 * guest since B3.10, each waiting on the owner's approval of the note that describes it.
 */
export type EventsMode = "off" | "self" | "on";

/** One of the game's telemetry events as it is recorded, or undefined for one that is not. */
export function recordedFrom(
  event: TelemetryEvent,
  at: number,
  game: string | undefined,
): RecordedEvent | undefined {
  const type = oneOf(RECORDED_TYPES, event.type);
  if (!type) {
    return undefined;
  }
  const data: Record<string, number | string | boolean> = {};
  for (const [key, value] of Object.entries(event)) {
    if (
      key !== "type" &&
      (typeof value === "number" || typeof value === "boolean" || typeof value === "string")
    ) {
      data[key] = typeof value === "string" ? value.slice(0, textLimit(type, key)) : value;
    }
  }
  return { type, at: Math.round(at), ...(game ? { game } : {}), data };
}

function dataOf(value: unknown, type: string): EventData | undefined {
  if (!isRecord(value)) {
    return undefined;
  }
  const entries = Object.entries(value);
  if (entries.length > TUNABLES.events.maxDataKeys) {
    return undefined;
  }
  const data: Record<string, number | string | boolean> = {};
  for (const [key, field] of entries) {
    if (key.length > MAX_ID_CHARS) {
      return undefined;
    }
    if (typeof field === "number" && Number.isFinite(field)) {
      data[key] = field;
    } else if (typeof field === "boolean") {
      data[key] = field;
    } else if (typeof field === "string" && field.length <= textLimit(type, key)) {
      data[key] = field;
    } else {
      return undefined;
    }
  }
  return data;
}

function eventOf(value: unknown): RecordedEvent | undefined {
  if (!isRecord(value)) {
    return undefined;
  }
  const type = oneOf(RECORDED_TYPES, value.type);
  // Milliseconds since the page loaded: any whole number from 0, however large.
  const at = count(value.at, Number.MAX_VALUE);
  const game = value.game === undefined ? undefined : text(value.game, MAX_ID_CHARS);
  const data = type ? dataOf(value.data, type) : undefined;
  if (!type || at === undefined || (value.game !== undefined && game === undefined) || !data) {
    return undefined;
  }
  return { type, at, ...(game ? { game } : {}), data };
}

/** A batch from a page, checked and rebuilt; undefined when it is not one. */
export function parseEventBatch(raw: string): EventBatch | undefined {
  if (raw.length > TUNABLES.events.maxBatchChars) {
    return undefined;
  }
  const value = jsonRecord(raw);
  if (!value) {
    return undefined;
  }
  const guest = text(value.guest, MAX_ID_CHARS);
  const session = text(value.session, MAX_ID_CHARS);
  const build = text(value.build, MAX_ID_CHARS);
  const listed = value.events;
  if (
    !guest ||
    !session ||
    !build ||
    !Array.isArray(listed) ||
    listed.length === 0 ||
    listed.length > TUNABLES.events.maxEvents
  ) {
    return undefined;
  }
  const events = listOf(listed, eventOf);
  return events ? { guest, session, build, events } : undefined;
}

/**
 * Whether this page sends its events: the build's `VITE_EVENTS` ("on", "self"; anything else
 * off), and `?events=on` for a "self" build or any dev or e2e build. `?events=off` always wins.
 */
export function eventsModeOf(
  search: string,
  built: string | undefined,
  devBuild: boolean,
): EventsMode {
  return switchOf("events", search, built, devBuild);
}

/**
 * One switch: the build's value (`VITE_<KEY>`: "on", "self"; anything else off), and
 * `?<key>=on` for a "self" build or any dev or e2e build. `?<key>=off` always wins.
 */
export function switchOf(
  key: string,
  search: string,
  built: string | undefined,
  devBuild: boolean,
): EventsMode {
  const asked = new URLSearchParams(search).get(key);
  if (asked === "off") {
    return "off";
  }
  if (built === "on") {
    return "on";
  }
  return asked === "on" && (built === "self" || devBuild) ? "self" : "off";
}

/** The events endpoint beside a lobby server: `wss://host` becomes `https://host/events`. */
export function eventsUrlOf(lobbyUrl: string): string {
  return `${lobbyUrl.replace(/^ws(s?):\/\//, "http$1://")}/events`;
}
